import React, { useEffect } from "react";
import { useOnboarding } from "src/store/features/onboarding.slice";
import * as Yup from "yup";
import useCompanyFormConfig from "./useAboutYourEntityConfig";
import {
  isUnitedStatesCountry,
  isUsResidenceStateBlockedForOnboarding,
  validStates,
} from "./tools";
import useSupportedCountries from "src/utils/hooks/useSupportedCountries";
import { rejectPoBox } from "src/utils/poBox";

const ONBOARDING_RESIDENCE_ISO2 = ["AU", "NZ", "US", "GB"] as const;

const ONBOARDING_APPLICATION_UNSUPPORTED_COUNTRY_MESSAGE =
  "This country is not supported for standard onboarding.";

const ONBOARDING_APPLICATION_UNSUPPORTED_US_STATE_MESSAGE =
  "This US state is not supported for onboarding.";

function isStandardOnboardingResidenceCountryLabel(
  label: string,
  allCountryRows: { value: string; label: string }[],
): boolean {
  const row = allCountryRows.find((c) => c.label === label);
  if (!row) return false;
  return (ONBOARDING_RESIDENCE_ISO2 as readonly string[]).includes(row.value);
}

export const isValidNumber = (value: string | undefined) => {
  return value !== "" && !isNaN(Number(value));
};

// Returns true if the given day/month/year make up a valid calendar date and
// the person is at least 18 years old today.
export const isAtLeast18 = (
  day: string | undefined,
  month: string | undefined,
  year: string | undefined,
): boolean => {
  if (!isValidNumber(day) || !isValidNumber(month) || !isValidNumber(year)) {
    // Let the "required" checks surface these — don't double-report as an age error.
    return true;
  }

  const dayNum = Number(day);
  const monthNum = Number(month);
  const yearNum = Number(year);

  const dob = new Date(yearNum, monthNum - 1, dayNum);
  // Reject impossible dates (e.g. 31 Feb rolls over to March).
  const isRealDate =
    dob.getFullYear() === yearNum &&
    dob.getMonth() === monthNum - 1 &&
    dob.getDate() === dayNum;
  if (!isRealDate) return true;

  const now = new Date();
  const eighteenthBirthday = new Date(dob.getFullYear() + 18, dob.getMonth(), dob.getDate());
  return eighteenthBirthday <= now;
};

function onboardingResidenceCountrySchema(
  allCountries: { value: string; label: string }[],
): Yup.StringSchema {
  return Yup.string()
    .required("Country is required")
    .test(
      "onboarding-country",
      ONBOARDING_APPLICATION_UNSUPPORTED_COUNTRY_MESSAGE,
      (value) => isStandardOnboardingResidenceCountryLabel(value ?? "", allCountries),
    );
}

function onboardingResidenceStateSchema(countryField: string): Yup.StringSchema {
  return Yup.string().when(countryField, {
    is: (val: string) => isUnitedStatesCountry(val),
    then: Yup.string()
      .required("State is required")
      .oneOf(validStates, "Type the abbreviated US state EG: AL")
      .test(
        "onboarding-us-state",
        ONBOARDING_APPLICATION_UNSUPPORTED_US_STATE_MESSAGE,
        (value) => !isUsResidenceStateBlockedForOnboarding(value),
      ),
    otherwise: Yup.string().required("State is required"),
  });
}

export default function useApplicationFormConfig() {
  const { applicationStep } = useOnboarding();
  const { allCountries } = useSupportedCountries();

  const [validationSchema, setValidationSchema] = React.useState<Yup.ObjectSchema<any>>(
    Yup.object(),
  );

  const { enableNatureOfBusiness } = useCompanyFormConfig();

  const entityValidationSchema = Yup.object().shape({
    entityName: Yup.string().required("Name is required"),
    entityId: Yup.string()
      .required("Identification number is required")
      .max(20, "ID must be 20 digits or lower"),
    country: onboardingResidenceCountrySchema(allCountries),
    state: onboardingResidenceStateSchema("country"),
    natureOfBusiness: enableNatureOfBusiness
      ? Yup.string()
          .max(255, "Max character length is 255")
          .required("Nature of Business is required")
      : Yup.string().max(255, "Max character length is 255"),
    address: Yup.string().required("Address is required"),
    postCode: Yup.string()
      .required("Post code is required")
      .max(10, "Post code must be 10 digits or lower"),
    streetAddress: rejectPoBox(Yup.string().required("Street Address is required")),
    unitApartment: rejectPoBox(),
  });

  const aboutYouValidationSchema = Yup.object().shape({
    birthDay: Yup.string().test("not-empty", "Day is required", isValidNumber),
    birthMonth: Yup.string().test("not-empty", "Month is required", isValidNumber),
    birthYear: Yup.string()
      .test("not-empty", "Year is required", isValidNumber)
      .test("age", "You must be at least 18 years old", function () {
        const { birthDay, birthMonth, birthYear } = this.parent;
        return isAtLeast18(birthDay, birthMonth, birthYear);
      }),
    country: onboardingResidenceCountrySchema(allCountries),
    nationality: Yup.string().required("Nationality is required"),
    state: onboardingResidenceStateSchema("country"),
    address: Yup.string().required("Address is required"),
    city: Yup.string().required("City is required"),
    postCode: Yup.string()
      .required("Post code is required")
      .max(10, "Post code must be 10 digits or lower"),
    streetAddress: rejectPoBox(Yup.string().required("Street Address is required")),
    unitApartment: rejectPoBox(),
    phone: Yup.string()
      .required("Phone number is required")
      .test("phone", "Invalid phone number, minimum 10 digit required", function (value) {
        if (value) {
          const modifiedValue = value.replace(/[\s()-+]/g, "");

          if (value.startsWith("+0")) {
            return this.createError({
              message: "Country code +0 does not exist.",
            });
          }

          return modifiedValue.length > 8;
        }

        return false;
      }),
    ssn: Yup.string()
      .min(9, "SSN must be at least 9 digits")
      .max(11, "SSN must be up to 11 digits if hyphens are included"),
  });

  const yourCryptJourneyValidationSchema = Yup.object().shape({
    country: onboardingResidenceCountrySchema(allCountries),
    state: onboardingResidenceStateSchema("country"),
    wealthSource: Yup.array()
      .of(Yup.string().required("Each selection must be a valid string"))
      .min(1, "At least one source must be selected")
      .required("Wealth source is required"),
    investmentExp: Yup.string().required("Please select your investment experience"),
    defaultCurrency: Yup.string().required("Please select your preferred currency"),
    occupation: Yup.string()
      .min(2, "Occupation must be at least 2 characters")
      .required("Occupation is required"),
    industry: Yup.string()
      .min(2, "Industry must be at least 2 characters")
      .required("Industry is required"),
    frequency: Yup.string().required("Frequency is required"),
    accountPurpose: Yup.array()
      .of(Yup.string().required("Each selection must be a valid string"))
      .min(1, "At least one purpose must be selected")
      .required("Account purpose is required"),
    hearAboutUs: Yup.string()
      .min(2, "Reference must be at least 2 characters")
      .required("Reference is required"),
    referralCode: Yup.string().when("hearAboutUs", {
      is: (val: string) =>
        val === "Referral Partner / Affiliate" ||
        val === "Online - social media" ||
        val === "Advertisement",
      then: Yup.string().min(1, "This field is required").required("This field is required"),
      otherwise: Yup.string(),
    }),
  });

  useEffect(() => {
    if ("aboutYourEntity" === applicationStep) setValidationSchema(entityValidationSchema);
    if ("aboutYou" === applicationStep) setValidationSchema(aboutYouValidationSchema);
    if ("cryptoJourney" === applicationStep) setValidationSchema(yourCryptJourneyValidationSchema);
  }, [applicationStep, enableNatureOfBusiness, allCountries]);

  return [validationSchema];
}
